import { Vec3 } from 'playcanvas';

import type { Pose } from './camera-poses';
import { Events } from './events';
import { extractExifMetadata } from './exif-reader';

type PictureId = string;

type PictureEvidenceMetadata = {
    exhibitId?: string,
    capturedAt?: string,
    capturedBy?: string,
    device?: string,
    tags?: string[],
    description?: string
};

type Picture = PictureEvidenceMetadata & {
    id: PictureId,
    name: string,
    frame: number,
    position: Vec3,
    target: Vec3,
    source?: string,
    validated: boolean,
    image?: {
        url?: string,
        filename?: string
    }
};

const hasOwn = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key);

const sanitizeString = (value: unknown) => (typeof value === 'string' ? value : undefined);

const sanitizeTags = (value: unknown) => {
    if (!Array.isArray(value)) {
        return undefined;
    }
    return value.filter((entry): entry is string => typeof entry === 'string');
};

const applyMetadataPatch = (pose: Pose, patch: Partial<PictureEvidenceMetadata>) => {
    let changed = false;

    if (hasOwn(patch, 'exhibitId')) {
        const next = sanitizeString(patch.exhibitId);
        if (pose.exhibitId !== next) {
            pose.exhibitId = next;
            changed = true;
        }
    }
    if (hasOwn(patch, 'capturedAt')) {
        const next = sanitizeString(patch.capturedAt);
        if (pose.capturedAt !== next) {
            pose.capturedAt = next;
            changed = true;
        }
    }
    if (hasOwn(patch, 'capturedBy')) {
        const next = sanitizeString(patch.capturedBy);
        if (pose.capturedBy !== next) {
            pose.capturedBy = next;
            changed = true;
        }
    }
    if (hasOwn(patch, 'device')) {
        const next = sanitizeString(patch.device);
        if (pose.device !== next) {
            pose.device = next;
            changed = true;
        }
    }
    if (hasOwn(patch, 'description')) {
        const next = sanitizeString(patch.description);
        if (pose.description !== next) {
            pose.description = next;
            changed = true;
        }
    }
    if (hasOwn(patch, 'tags')) {
        const next = sanitizeTags(patch.tags);
        const prev = pose.tags;
        const sameLength = (prev?.length ?? 0) === (next?.length ?? 0);
        const sameValues = sameLength && (prev ?? []).every((entry, index) => entry === (next ?? [])[index]);
        if (!sameValues) {
            pose.tags = next;
            changed = true;
        }
    }

    return changed;
};

const registerPicturesEvents = (events: Events) => {
    let selectedId: PictureId | null = null;

    const imageById = new Map<PictureId, { url: string; filename?: string }>();
    const exifParseTokenById = new Map<PictureId, number>();

    // Visible only when picture mode is enabled; stored independently for persistence.
    let cuesEnabled = false;
    let overlayEnabled = false;

    const poseToPictureId = (pose: Pose): PictureId => String(pose.frame);

    const listPictures = (): Picture[] => {
        const poses = (events.invoke('camera.poses') as Pose[]) ?? [];
        return poses.map((p) => {
            const id = poseToPictureId(p);
            const img = imageById.get(id);
            return {
                id,
                name: p.name,
                frame: p.frame,
                position: p.position,
                target: p.target,
                source: p.source,
                validated: p.validated !== false,
                image: img ? { url: img.url, filename: img.filename } : undefined,
                exhibitId: p.exhibitId,
                capturedAt: p.capturedAt,
                capturedBy: p.capturedBy,
                device: p.device,
                tags: p.tags?.slice(),
                description: p.description
            };
        });
    };

    const findPictureById = (id: PictureId | null): Picture | null => {
        if (!id) return null;
        return listPictures().find(p => p.id === id) ?? null;
    };

    const findPoseById = (id: PictureId) => {
        const poses = (events.invoke('camera.poses') as Pose[]) ?? [];
        return poses.find(entry => String(entry.frame) === String(id));
    };

    const setSelectedId = (id: PictureId | null) => {
        const next = id;
        if (next === selectedId) return;
        selectedId = next;
        events.fire('pictures.selectedChanged', selectedId);
    };

    const getSelectedPicture = (): Picture | null => findPictureById(selectedId);

    const isPictureModeUsable = () => {
        const enabled = !!events.invoke('picture.enabled');
        const needsValidation = !!events.invoke('camera.posesNeedsValidation');
        return enabled && !needsValidation;
    };

    const focusPicture = (picture: Picture) => {
        events.fire('camera.setPose', { position: picture.position, target: picture.target }, 0);
    };

    const selectPicture = (id: PictureId | null) => {
        if (id === null) {
            setSelectedId(null);
            return;
        }

        const pic = findPictureById(id);
        if (!pic) {
            setSelectedId(null);
            return;
        }

        setSelectedId(pic.id);
    };

    const nearestPictureId = (): PictureId | null => {
        if (!isPictureModeUsable()) return null;

        const pictures = listPictures().filter(p => p.validated !== false);
        if (pictures.length === 0) return null;

        const camPose = events.invoke('camera.getPose') as any;
        const cp = camPose?.position;
        if (!cp) return null;

        const camPos = new Vec3(cp.x, cp.y, cp.z);

        let best: Picture | null = null;
        let bestD2 = Infinity;
        for (const p of pictures) {
            const dx = camPos.x - p.position.x;
            const dy = camPos.y - p.position.y;
            const dz = camPos.z - p.position.z;
            const d2 = dx * dx + dy * dy + dz * dz;
            if (d2 < bestD2) {
                bestD2 = d2;
                best = p;
            }
        }

        return best?.id ?? null;
    };

    const setCuesEnabled = (enabled: boolean) => {
        const v = !!enabled;
        if (v === cuesEnabled) return;
        cuesEnabled = v;
        events.fire('pictures.cues.enabledChanged', cuesEnabled);
    };

    const setOverlayEnabled = (enabled: boolean) => {
        const v = !!enabled;
        if (v === overlayEnabled) return;
        overlayEnabled = v;
        events.fire('pictures.overlay.enabledChanged', overlayEnabled);
    };

    // API

    events.function('pictures.list', () => listPictures());
    events.function('pictures.count', () => listPictures().length);

    events.function('pictures.imageUrl', (id: PictureId | null) => {
        if (!id) return null;
        return imageById.get(id)?.url ?? null;
    });

    events.on('pictures.setImageForId', (id: PictureId, file: File, filename?: string) => {
        if (!id || !file) return false;

        const parseToken = (exifParseTokenById.get(id) ?? 0) + 1;
        exifParseTokenById.set(id, parseToken);

        const prev = imageById.get(id);
        if (prev?.url) {
            try {
                URL.revokeObjectURL(prev.url);
            } catch { /* noop */ }
        }

        const url = URL.createObjectURL(file);
        imageById.set(id, { url, filename: filename ?? file.name });
        events.fire('pictures.imageChanged', id);

        (async () => {
            const extracted = await extractExifMetadata(file);
            if (!extracted) {
                return;
            }

            if (exifParseTokenById.get(id) !== parseToken) {
                return;
            }

            const pose = findPoseById(id);
            if (!pose) {
                return;
            }

            const patch: Partial<PictureEvidenceMetadata> = {};

            // Overwrite policy: EXIF auto-fill never overwrites existing values.
            if (!pose.capturedAt && extracted.capturedAt) {
                patch.capturedAt = extracted.capturedAt;
            }
            if (!pose.device && extracted.device) {
                patch.device = extracted.device;
            }

            if (Object.keys(patch).length > 0) {
                events.fire('pictures.updateMetadata', id, patch);
            }
        })().catch(() => {});

        return true;
    });

    events.on('pictures.clearImages', () => {
        for (const v of imageById.values()) {
            if (v?.url) {
                try {
                    URL.revokeObjectURL(v.url);
                } catch { /* noop */ }
            }
        }
        imageById.clear();
        exifParseTokenById.clear();
        events.fire('pictures.listChanged');
    });

    events.on('pictures.updateMetadata', (id: PictureId, patch: Partial<PictureEvidenceMetadata>) => {
        if (!id || !patch || typeof patch !== 'object') {
            return false;
        }

        const poses = (events.invoke('camera.poses') as Pose[]) ?? [];
        const pose = poses.find(entry => String(entry.frame) === String(id));
        if (!pose) {
            return false;
        }

        const changed = applyMetadataPatch(pose, patch);
        if (!changed) {
            return true;
        }

        events.fire('camera.posesChanged', poses);
        events.fire('pictures.metadataChanged', id);
        events.fire('pictures.listChanged');
        return true;
    });

    events.function('pictures.selectedId', () => selectedId);
    events.function('pictures.selected', () => getSelectedPicture());

    events.on('pictures.select', (id: PictureId | null) => selectPicture(id));
    events.on('pictures.clearSelection', () => selectPicture(null));

    events.function('pictures.nearest', () => nearestPictureId());
    events.on('pictures.selectNearest', () => {
        const id = nearestPictureId();
        if (!id) return false;
        selectPicture(id);
        const pic = findPictureById(id);
        if (pic) focusPicture(pic);
        return true;
    });

    events.on('pictures.focusSelected', () => {
        if (!isPictureModeUsable()) return false;
        const pic = getSelectedPicture();
        if (!pic) return false;
        if (pic.validated === false) return false;
        focusPicture(pic);
        return true;
    });

    events.function('pictures.cues.enabled', () => cuesEnabled);
    events.on('pictures.cues.setEnabled', (enabled: boolean) => setCuesEnabled(!!enabled));
    events.on('pictures.cues.toggle', () => setCuesEnabled(!cuesEnabled));

    events.function('pictures.overlay.enabled', () => overlayEnabled);
    events.on('pictures.overlay.setEnabled', (enabled: boolean) => setOverlayEnabled(!!enabled));
    events.on('pictures.overlay.toggle', () => setOverlayEnabled(!overlayEnabled));

    // Keep selection coherent when poses change.
    events.on('camera.posesChanged', () => {
        // Prune images that no longer map to any picture id.
        const existing = new Set(listPictures().map(p => p.id));
        for (const id of imageById.keys()) {
            if (!existing.has(id)) {
                const prev = imageById.get(id);
                if (prev?.url) {
                    try {
                        URL.revokeObjectURL(prev.url);
                    } catch { /* noop */ }
                }
                imageById.delete(id);
            }
        }

        if (!selectedId) {
            events.fire('pictures.listChanged');
            return;
        }
        const pic = findPictureById(selectedId);
        if (!pic) {
            setSelectedId(null);
        }
        events.fire('pictures.listChanged');
    });

    events.on('scene.clear', () => {
        setSelectedId(null);
        events.fire('pictures.clearImages');
        exifParseTokenById.clear();
        events.fire('pictures.listChanged');
    });

    // Broadcast initial state for UI.
    events.fire('pictures.cues.enabledChanged', cuesEnabled);
    events.fire('pictures.overlay.enabledChanged', overlayEnabled);
    events.fire('pictures.selectedChanged', selectedId);
    events.fire('pictures.listChanged');
};

export { registerPicturesEvents };
export type { Picture, PictureId, PictureEvidenceMetadata };
